import { useMemo } from "react";
import { X } from "@phosphor-icons/react";
import { useLang } from "../../contexts/LanguageContext";

/**
 * Tâche 1398 (Eric 2026-09-29) — « Ouvre les deux transcriptions côte à côte
 * quand je clique sur un badge de désaccord. »
 *
 * On n'affiche QUE les tours du locuteur concerné, et seulement dans les deux
 * sources qui se contredisent : le désaccord porte sur cette personne-là, pas
 * sur la vidéo entière. Sortir les 800 tours des trois sources noierait la
 * seule chose à regarder.
 *
 * Les deux colonnes sont alignées sur le TEMPS, pas ligne à ligne : les
 * diariseurs ne découpent pas aux mêmes endroits, empiler les tours deux par
 * deux ferait diverger l'affichage dès le premier écart et donnerait à voir
 * un faux décalage. Chaque tour est donc posé avec son horodatage, et c'est
 * lui qui permet de comparer.
 */

const mmss = (s) => {
  const t = Math.max(0, Math.round(Number(s) || 0));
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};

const motsDe = (txt) => ((txt || "").trim().match(/\S+/g) || []).length;

export function SpeakerDivergenceModal({
  open,
  onClose,
  name,
  pair,
  sourceLabels,
  segmentsBySource,
  stats,
  onSeek,
}) {
  const { tr, lang } = useLang();

  const colonnes = useMemo(() => {
    if (!pair) return [];
    return pair.map((src) => {
      const tours = (segmentsBySource?.[src] || [])
        .filter((s) => (s.speaker || "") === name)
        .slice()
        .sort((a, b) => (a.start || 0) - (b.start || 0));
      return {
        src,
        tours,
        seconds: tours.reduce((n, s) => n + Math.max(0, (s.end || 0) - (s.start || 0)), 0),
        words: tours.reduce((n, s) => n + motsDe(s.text), 0),
      };
    });
  }, [pair, segmentsBySource, name]);

  if (!open || !pair) return null;

  const [a, b] = colonnes;
  const ecart = (x, y) => {
    const max = Math.max(x, y);
    return max === 0 ? 0 : Math.round((1 - Math.min(x, y) / max) * 100);
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="speaker-divergence-modal-overlay"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="speaker-divergence-modal"
      >
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-zinc-200">
          <div>
            <h3 className="text-base font-semibold text-zinc-900" data-testid="speaker-divergence-modal-title">
              {tr("Ce que chaque source attribue à ", "What each source attributes to ")}
              <span className="text-[#002FA7]">{name}</span>
            </h3>
            <p className="text-xs text-zinc-600 mt-1">
              {tr(
                `${sourceLabels[pair[0]]} et ${sourceLabels[pair[1]]} ne rapportent pas la même parole. Les tours sont posés avec leur horodatage : c'est lui qui permet de les rapprocher, pas leur ordre.`,
                `${sourceLabels[pair[0]]} and ${sourceLabels[pair[1]]} do not report the same speech. Turns are shown with their timestamp: that is what lets you line them up, not their order.`,
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-700 shrink-0"
            aria-label={tr("Fermer", "Close")}
            data-testid="speaker-divergence-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-zinc-100 bg-zinc-50 text-xs text-zinc-700 flex flex-wrap gap-x-6 gap-y-1">
          <span data-testid="speaker-divergence-modal-gap-seconds">
            <strong>{ecart(a.seconds, b.seconds)} %</strong>
            {tr(" d'écart sur le temps de parole", " gap on speaking time")}
            {` (${mmss(a.seconds)} / ${mmss(b.seconds)})`}
          </span>
          <span data-testid="speaker-divergence-modal-gap-words">
            <strong>{ecart(a.words, b.words)} %</strong>
            {tr(" sur le nombre de mots", " on word count")}
            {` (${a.words} / ${b.words})`}
          </span>
          {stats && (
            <span className="text-zinc-500">
              {tr("Tours : ", "Turns: ")}
              {a.tours.length} / {b.tours.length}
              {tr(" — un écart de tours seul ne veut rien dire", " — a turn gap alone means nothing")}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-px bg-zinc-200 overflow-y-auto flex-1">
          {colonnes.map((col) => (
            <div key={col.src} className="bg-white" data-testid={`speaker-divergence-col-${col.src}`}>
              <div className="sticky top-0 bg-white px-4 py-2 border-b border-zinc-200 text-xs font-semibold text-zinc-800">
                {sourceLabels[col.src]}
                <span className="ml-2 font-normal text-zinc-500">
                  {col.tours.length} {tr("tours", "turns")} · {mmss(col.seconds)} · {col.words} {tr("mots", "words")}
                </span>
              </div>
              {col.tours.length === 0 ? (
                <p className="px-4 py-6 text-xs text-zinc-500" data-testid={`speaker-divergence-empty-${col.src}`}>
                  {tr(
                    `${sourceLabels[col.src]} n'attribue aucun tour à ce nom.`,
                    `${sourceLabels[col.src]} attributes no turn to this name.`,
                  )}
                </p>
              ) : (
                <ul className="divide-y divide-zinc-100">
                  {col.tours.map((t, i) => (
                    <li key={`${col.src}-${i}`} className="px-4 py-2">
                      <button
                        type="button"
                        onClick={() => onSeek && onSeek(t.start)}
                        className="text-[10px] font-mono text-[#002FA7] hover:underline"
                        data-testid={`speaker-divergence-seek-${col.src}-${i}`}
                      >
                        {mmss(t.start)} → {mmss(t.end)}
                      </button>
                      <p className="text-xs text-zinc-700 leading-relaxed mt-0.5">{t.text}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        <div className="px-5 py-3 border-t border-zinc-200 text-[11px] text-zinc-500">
          {lang === "fr"
            ? "Si les deux colonnes racontent la même chose autrement découpée, il n'y a rien à corriger. Si l'une attribue à ce nom une parole que l'autre donne à quelqu'un d'autre, c'est là qu'il faut renommer ou fusionner."
            : "If both columns tell the same story cut differently, there is nothing to fix. If one attributes speech to this name that the other gives to someone else, that is where you rename or merge."}
        </div>
      </div>
    </div>
  );
}

export default SpeakerDivergenceModal;
